import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Modal, Space } from 'antd';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../webux/webux-layouts/WebUxPage';
import { DataTable, DataTablePagination } from '../../components/molecules/DataTable/DataTable';
import { QueryBuilder } from '../../components/molecules/QueryBuilder/QueryBuilder';
import { EnumType, EnumValue } from '../../entity/Enum';
import { useConnection } from '../../connection/Application';
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { InfoView, InfoViewItem } from '../../components/info-view/InfoView';
import './enum-x.less';
import { FlexBox } from '../../components/atoms/FlexBox';
import Form, { FormSchema } from '../../form/Form';
import { FormModal, useFormModal } from '../../components/form-modal/FormModal';
import { PageHeader } from '@ant-design/pro-layout';

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
  },
];

const schema: FormSchema = [
  {
    id: 'name',
    name: 'Name',
    type: 'string',
  },
  {
    id: 'description',
    name: 'Description',
    type: 'text',
  },
];

const enumValueSchema: FormSchema = [
  {
    id: 'name',
    name: 'Name',
    type: 'string',
  },
  {
    id: 'value',
    name: 'Value',
    type: 'string',
  },
];

const EnumX: FC = () => {
  const [enumVisible, setEnumVisible] = useState<boolean>(false);
  const [filter, setFilter] = useState<any>({});
  const [currentValue, setCurrentValue] = useState<EnumType>();
  const [data, setData] = useState([]);
  const [enumValueData, setEnumValueData] = useState<EnumValue[]>();
  const [enumValueVisible, setEnumValueVisible] = useState<boolean>(false);
  const [editValue, setEditValue] = useState<EnumValue>();
  const [loading, setLoading] = useState<boolean>();
  const [total, setTotal] = useState<number>(0);
  const [pagination, setPagination] = useState<DataTablePagination>({ page: 1, size: 10 });
  const connection = useConnection();
  const addEnumForm = useFormModal<EnumType>();
  const editEnumForm = useFormModal<EnumType>();
  const addValueForm = useFormModal<EnumValue>();
  const editValueForm = useFormModal<EnumValue>();

  const search = useCallback(
    (filter: any, pagination: DataTablePagination) => {
      setLoading(true);
      connection
        .get('admin/enum', { ...pagination, ...filter })
        .then((response) => {
          setData(response.enums.map((e: any) => ({ ...e, key: e.id })));
          setTotal(response.total);
        })
        .finally(() => setLoading(false));
    },
    [connection],
  );

  useEffect(() => {
    // connection.get('admin/enum').then((response) => setData(response.enums));
    search({}, { page: 1, size: 10 });
  }, [search]);

  const getEnumValue = useCallback(
    (id?: number) => {
      setEnumValueData(undefined);
      connection.get(`admin/enum-value/${id}`).then((response) => {
        setEnumValueData(response);
      });
    },
    [connection],
  );

  const deleteEnumValue = useCallback(
    (valueID: number) => {
      Modal.confirm({
        title: 'Delete Enum Value',
        icon: <ExclamationCircleOutlined />,
        content: 'Are you sure delete to this value?',
        okText: 'Delete',
        okType: 'danger',
        onOk() {
          connection
            .delete(`admin/enum-value/${valueID}`)
            .then(() => getEnumValue(currentValue?.id));
        },
        onCancel() {
          console.log('Cancel');
        },
      });
    },
    [connection, currentValue?.id, getEnumValue],
  );
  const addEnumType = useCallback(
    async (enumType: EnumType) => {
      try {
        await connection.post('admin/enum', enumType);
        addEnumForm.close();
        search({}, { page: 1, size: 10 });
      } catch (e: any) {
        if (e.body?.body?.errors) {
          addEnumForm.setErrors(e.body.body.errors);
        } else {
          addEnumForm.setErrors([]);
        }
      }
    },
    [addEnumForm, connection, search],
  );
  const editEnumType = useCallback(
    async (enumType: EnumType) => {
      if (currentValue && currentValue.id) {
        try {
          await connection.patch(`admin/enum/${currentValue.id}`, enumType);
          editEnumForm.close();
          search({}, { page: 1, size: 10 });
        } catch (e: any) {
          if (e.body?.body?.errors) {
            editEnumForm.setErrors(e.body.body.errors);
          } else {
            editEnumForm.setErrors([]);
          }
        }
      }
    },
    [connection, currentValue, editEnumForm, search],
  );

  const addEnumValue = useCallback(
    async (enumValue: EnumValue) => {
      try {
        enumValue.enumType = currentValue?.id as number;
        await connection.post('admin/enum-value', enumValue);
        getEnumValue(currentValue?.id);
        addValueForm.close();
      } catch (e: any) {
        if (e.body?.body?.errors) {
          addValueForm.setErrors(e.body.body.errors);
        } else {
          addValueForm.setErrors([]);
        }
      }
    },
    [addValueForm, connection, currentValue?.id, getEnumValue],
  );

  const editEnumValue = useCallback(
    async (enumValue: EnumValue) => {
      try {
        enumValue.enumType = editValue?.enumType as number;
        await connection.patch(`admin/enum-value/${editValue?.id}`, enumValue);
        setEditValue(undefined);
        getEnumValue(currentValue?.id);
        editValueForm.close();
      } catch (e: any) {
        if (e.body?.body?.errors) {
          editValueForm.setErrors(e.body.body.errors);
        } else {
          editValueForm.setErrors([]);
        }
      }
    },
    [connection, currentValue?.id, editValue?.enumType, editValue?.id, editValueForm, getEnumValue],
  );

  return (
    <WebUxPage>
      <PageHeader ghost={false} style={{ background: '#FFF' }} title="Enum" subTitle="Enum" />
      <WebUxPageContent>
        <WebUxPageItem
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <QueryBuilder
            searchPlaceholder="Search Enum"
            search={true}
            value={filter}
            onChange={setFilter}
            onSearch={(value) => {
              setPagination({ page: 1, size: 10 });
              search(value, pagination);
            }}
            schema={[]}
          />
          <Space>
            <Button
              onClick={() => {
                setFilter({});
                setPagination({ page: 1, size: 10 });
                search({}, { page: 1, size: 10 });
              }}>
              Reset
            </Button>
            <Button type="primary">Search</Button>
          </Space>
        </WebUxPageItem>
        <WebUxPageItem>
          <Space direction={'vertical'} style={{ width: '100%' }} size={16}>
            <FlexBox horizontalAlign={'right'}>
              <Button
                icon={<PlusOutlined />}
                key="1"
                type="primary"
                onClick={() => addEnumForm.open()}>
                New Enum
              </Button>
            </FlexBox>
            <DataTable
              columns={columns}
              total={total}
              loading={loading}
              dataSource={data}
              onPaginationChange={(p) => {
                setPagination(p);
                if (pagination.page !== p.page || pagination.size !== p.size) {
                  search(filter, p);
                }
              }}
              onClick={(item: EnumType) => {
                setCurrentValue(item);
                getEnumValue(item.id);
                setEnumVisible(true);
              }}
              dtPagination={pagination}
            />
          </Space>
        </WebUxPageItem>
      </WebUxPageContent>
      <Modal
        className="enum-view-modal"
        open={enumVisible}
        title={'Enum'}
        onCancel={() => setEnumVisible(false)}
        footer={
          <Space align={'end'}>
            <Button onClick={() => setEnumVisible(false)}>Close</Button>
            <Button onClick={() => editEnumForm.open(currentValue)}>Edit Type</Button>
            <Button
              type="primary"
              onClick={() => {
                setEnumValueVisible(true);
                setEnumVisible(false);
              }}>
              Edit Values
            </Button>
          </Space>
        }>
        <InfoView>
          <InfoViewItem title={(currentValue && currentValue.name) ?? ''}>
            {(currentValue && currentValue.description) ?? ''}
          </InfoViewItem>
          <InfoViewItem title={'Enum Values'}>
            <div style={{ height: 8 }} />

            {enumValueData &&
              enumValueData.map((item) => {
                return (
                  <div key={item.id} style={{ flexDirection: 'row', display: 'flex' }}>
                    <p style={{ fontSize: 16 }}>{item.name}</p>
                  </div>
                );
              })}
          </InfoViewItem>
        </InfoView>
      </Modal>

      <Modal
        open={enumValueVisible}
        title={currentValue?.name ?? 'Enum Value'}
        footer={
          <Space align={'end'}>
            <Button
              onClick={() => {
                setEnumValueVisible(false);
                setEnumVisible(true);
                // setEnumValueEditable(false);
              }}>
              Close
            </Button>
            <Button type="primary" onClick={() => addValueForm.open()}>
              Add New
            </Button>
          </Space>
        }>
        {enumValueData &&
          enumValueData.map((item) => {
            return (
              <div key={item.id} className="modal-content">
                <div>
                  <p>{item.name}</p>
                </div>
                <div style={{ flexDirection: 'row', display: 'flex' }}>
                  <div>
                    <EditOutlined
                      onClick={() => {
                        setEditValue(item);
                        editValueForm.open(item);
                      }}
                    />
                  </div>
                  <div style={{ paddingLeft: 12 }}>
                    <DeleteOutlined onClick={() => deleteEnumValue(item.id ?? 0)} />
                  </div>
                </div>
              </div>
            );
          })}
      </Modal>

      <FormModal title={'Create Enum'} onSubmit={addEnumType} form={addEnumForm}>
        <Form.Items schema={schema} />
      </FormModal>
      <FormModal title={'Edit Enum'} onSubmit={editEnumType} form={editEnumForm}>
        <Form.Items schema={schema} />
      </FormModal>
      <FormModal title={'Add Enum Value'} onSubmit={addEnumValue} form={addValueForm}>
        <Form.Items schema={enumValueSchema} />
      </FormModal>
      <FormModal title={'Edit Value'} onSubmit={editEnumValue} form={editValueForm}>
        <Form.Items schema={enumValueSchema} />
      </FormModal>
    </WebUxPage>
  );
};

export default EnumX;
